import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatMoney } from '../util/helpers/numeralHelpers';
import { selectSettings } from '../store/selectors/settingsSelectors';
import { APPLICATION_FUNDING_TYPE } from '../util/constants';
import useAipFlow from './useAipFlow';
import PaperEditIcon from '../components/Icons/PaperEditIcon';
import ChainIcon from '../components/Icons/ChainIcon';
import SavingBankIcon from '../components/Icons/SavingBankIcon';
import DataTransferIcon from '../components/Icons/DataTransferIcon';
import MailboxInIcon from '../components/Icons/MailboxInIcon';
import { PAGES } from '../constants/pages';
import { ACCOUNT_STEP_VIEW } from '../util/gtmEvents';
import useGtmStepHook from '../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../constants/sessionStorage';
import { gtmStepHelper } from '../util/helpers/gtmHelper';
import { useApplicantData } from './useApplicantData';

const useStandardFundingOptions = (
  openPlaid,
  submit,
  useAccountBalanceFunding,
  isSalesAssisted,
) => {
  const { t } = useTranslation();
  const {
    AchDepositLimit,
    CheckDepositLimit,
    MetalStreamRecurringInvestment,
  } = useSelector(selectSettings);
  const { isAipFlow } = useAipFlow();
  const ApplicantData = useApplicantData();
  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  const ordinal = (number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = number % 100;
    return number + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const handleGtmEvent = (methodType) => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: `Fund your account - ${methodType}`,
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const fundMetalOptions = isSalesAssisted
    ? [
        {
          title: t('fundAccount.manualMetalTitle'),
          description: t('fundAccount.manualMetalDescription', {
            reccuringInvestment: ordinal(MetalStreamRecurringInvestment),
          }),
          Icon: PaperEditIcon,
          showDivider: false,
          fundType: 'manual',
          onClick: useAccountBalanceFunding,
        },
      ]
    : [
        {
          title: t('fundAccount.addMetalBankTitle'),
          description: t('fundAccount.addMetalBankDescription'),
          recommended: true,
          Icon: ChainIcon,
          fundType: 'plaid',
          onClick: () => {
            openPlaid();
            handleGtmEvent('ACH');
          },
          dataCy: 'button-connect-a-bank-account',
        },
        {
          title: t('fundAccount.manualMetalTitle'),
          description: t('fundAccount.manualMetalDescription', {
            reccuringInvestment: ordinal(MetalStreamRecurringInvestment),
          }),
          Icon: PaperEditIcon,
          showDivider: false,
          fundType: 'manual',
          onClick: useAccountBalanceFunding,
        },
      ];
  const normalFundAccountOptions = isSalesAssisted
    ? [
        {
          title: t('fundAccount.wireTitle'),
          subtitle: t('fundAccount.wireSubtitle'),
          description: t('fundAccount.wireDescription'),
          Icon: DataTransferIcon,
          fundType: APPLICATION_FUNDING_TYPE[2],
          onClick: () => {
            submit(
              APPLICATION_FUNDING_TYPE[2],
              PAGES.FUNDING.NORMAL_FUNDING.AMOUNT.WIRE,
            );
            handleGtmEvent('Wire');
          },
          dataCy: 'button-wire-transfer',
        },
        {
          title: t('fundAccount.checkTitle'),
          subtitle: t('fundAccount.checkSubtitle', {
            limit: formatMoney(CheckDepositLimit, 0, 0),
          }),
          description: t('fundAccount.checkDescription'),
          Icon: MailboxInIcon,
          fundType: APPLICATION_FUNDING_TYPE[3],
          onClick: () => {
            submit(
              APPLICATION_FUNDING_TYPE[3],
              PAGES.FUNDING.NORMAL_FUNDING.AMOUNT.CHECK,
            );
            handleGtmEvent('Check');
          },
          dataCy: 'button-check-transfer',
        },
      ]
    : [
        {
          title: t('fundAccount.addBankTitle'),
          subtitle: t('fundAccount.addBankSubtitle', {
            limit: formatMoney(AchDepositLimit, 0, 0),
          }),
          description: t('fundAccount.addBankDescription'),
          recommended: true,
          Icon: SavingBankIcon,
          fundType: APPLICATION_FUNDING_TYPE[0],
          onClick: () => {
            openPlaid();
            handleGtmEvent('ACH');
          },
          dataCy: 'button-connect-a-bank-account',
        },
        {
          title: t('fundAccount.wireTitle'),
          subtitle: t('fundAccount.wireSubtitle'),
          description: t('fundAccount.wireDescription'),
          Icon: DataTransferIcon,
          fundType: APPLICATION_FUNDING_TYPE[2],
          onClick: () => {
            submit(
              APPLICATION_FUNDING_TYPE[2],
              PAGES.FUNDING.NORMAL_FUNDING.AMOUNT.WIRE,
            );
            handleGtmEvent('Wire');
          },
          dataCy: 'button-wire-transfer',
        },
        {
          title: t('fundAccount.checkTitle'),
          subtitle: t('fundAccount.checkSubtitle', {
            limit: formatMoney(CheckDepositLimit, 0, 0),
          }),
          description: t('fundAccount.checkDescription'),
          Icon: MailboxInIcon,
          fundType: APPLICATION_FUNDING_TYPE[3],
          onClick: () => {
            submit(
              APPLICATION_FUNDING_TYPE[3],
              PAGES.FUNDING.NORMAL_FUNDING.AMOUNT.CHECK,
            );
            handleGtmEvent('Check');
          },
          dataCy: 'button-check-transfer',
        },
      ];

  if (isAipFlow) {
    return fundMetalOptions;
  }
  return normalFundAccountOptions;
};

export default useStandardFundingOptions;
